import { useCallback, useEffect } from 'react';

import { useCreateRovoExperienceSidebarController } from '@confluence/ai-rovo-button/entry-points/useCreateRovoExperienceSidebarController.ts';

import { AGENT_MENTION_COMMENT_SAVED_EVENT } from './agentMentionConstants.ts';
import type { AgentMentionInfo } from './agentMentionUtils.ts';

/**
 * Listens for agent @mention comment save events and opens the CwR sidebar.
 *
 * The actual chat-new payload (with prompt and agentId) is delivered separately
 * via the rovo-triggers PubSub system — this hook only handles opening the
 * sidebar panel itself.
 *
 * Must be mounted in a component that has access to the right sidebar context
 * (e.g., ViewPageRovoConfigProvider).
 */
export function useAgentMentionSidebarListener(): void {
	const sidebarApi = useCreateRovoExperienceSidebarController();

	const handleAgentMentionSaved = useCallback(
		(event: Event) => {
			const detail = (event as CustomEvent<AgentMentionInfo>).detail;
			if (!detail) {
				return;
			}
			sidebarApi.openSidebar();
		},
		[sidebarApi],
	);

	useEffect(() => {
		window.addEventListener(AGENT_MENTION_COMMENT_SAVED_EVENT, handleAgentMentionSaved);

		return () => {
			window.removeEventListener(AGENT_MENTION_COMMENT_SAVED_EVENT, handleAgentMentionSaved);
		};
	}, [handleAgentMentionSaved]);
}
